<template>
  <div
    v-if="hasItinerary"
    class="flight-box"
    :class="{ 'flight-box__inactive': (otherFlightActive && !active) || selectedFlightGroup, 'flight-box__recommended': recommended }"
  >
    <strong
      v-if="recommended"
      :data-i18n="`charterResult${activeFlightOffer}`"
    >{{ $t(`charterResult${activeFlightOffer}`) }}</strong>
    <div class="flex flex-wrap -mx-4">
      <LazyBanFlightButton
        v-if="gfids"
        class="absolute right-0 top-0"
        :gfids="gfids"
      />
      <CharterItineraryFlightRow
        type="departure"
        :primary="true"
        :flight-choice="flightChoice"
        :flight-info="flightOffer.itinieraries[0]"
        :flights-collapsed="flightsCollapsed"
        :luggage="outboundLuggage"
        :expanded="expanded"
        :parent-class="['px-4', { 'lg:w-6/12': !flightChoice, 'lg:w-5/12': flightChoice }]"
        :show-luggage="true"
        :luggage-policies="luggagePolicies"
        @expand="expanded = true"
        @collapse="expanded = false"
      />
      <CharterItineraryFlightRow
        v-if="flightOffer.itinieraries[1]"
        type="arrival"
        :primary="true"
        :flight-choice="flightChoice"
        :flight-info="flightOffer.itinieraries[1]"
        :flights-collapsed="flightsCollapsed"
        :luggage="returnLuggage"
        :expanded="expanded"
        :parent-class="['px-4', { 'lg:w-6/12': !flightChoice, 'lg:w-5/12': flightChoice }]"
        :show-luggage="true"
        :luggage-policies="luggagePolicies"
        @expand="expanded = true"
        @collapse="expanded = false"
      />
      <div
        v-if="flightChoice"
        class="flight-box-column lg:w-2/12 px-4 text-center"
      >
        <template v-if="!active">
          <p
            v-if="priceOffset >= 0"
            class="font-bold text-center"
          >
            +{{ $n(priceOffset) }}
          </p>
          <p
            v-else
            class="font-bold text-center"
          >
            {{ $n(priceOffset) }}
          </p>
        </template>
        <button
          class="btn btn-flight-selection"
          :class="{ active: active }"
          @click="$emit('selectFlight')"
        >
          <div
            class="transform-uppercase w-full"
            :data-i18n="active ? 'charterChosenFlight' : 'charterChooseFlight'"
          >
            {{ $t(active ? 'charterChosenFlight' : 'charterChooseFlight') }}
          </div>
          <fa :icon="active ? icons.faCheckSquare : icons.faSquare" />
        </button>
        <a
          v-if="active"
          class="link-underline text-black"
          data-i18n="charterDeselectFlight"
          @click.stop="$emit('deselectFlight')"
        >{{ $t('charterDeselectFlight') }}</a>
      </div>
      <div
        v-if="flightsCollapsed"
        class="flight-box-column w-12/12 text-center"
      >
        <button
          class="btn btn-text btn-small"
          data-i18n="charterExpandFlights"
          @click="$emit('expandFlights')"
        >
          <fa icon="chevron-down" />
          <span class="ml-2">
            {{ $t('charterExpandFlights') }}
          </span>
        </button>
      </div>
      <CharterItinieraryWarnings :warnings="flightOffer.warningTextKeys" />
    </div>
  </div>
</template>

<script>
import { faSquare, faCheckSquare } from '@fortawesome/pro-regular-svg-icons'

export default defineNuxtComponent({
  name: 'CharterItinerarySingle',

  props: {
    flightOffer: {
      type: Object,
      default: () => ({ itinieraries: [] }),
    },

    flightChoice: {
      type: Boolean,
      default: false,
    },

    otherFlightActive: {
      type: Boolean,
      default: false,
    },

    selectedFlight: {
      type: Object,
      default: null,
    },

    selectedFlightGroup: {
      type: Array,
      default: null,
    },

    flightsCollapsed: {
      type: Boolean,
      default: false,
    },

    recommended: {
      type: Boolean,
      default: false,
    },

    activeFlightOffer: {
      type: String,
      default: null,
    },

    luggagePolicies: {
      type: Array,
      default: null,
    },

    gfids: {
      type: Array,
      default: null,
    },
  },

  emits: ['expandFlights', 'deselectFlight', 'selectFlight'],

  data () {
    return {
      icons: {
        faSquare,
        faCheckSquare,
      },

      windowWidth: 0,
      expanded: false,
    }
  },

  computed: {
    hasItinerary () {
      return this.flightOffer && this.flightOffer.itinieraries.length
    },

    active () {
      return this.selectedFlight && this.selectedFlight.id === this.flightOffer.id
    },

    priceOffset () {
      return !this.selectedFlight ? 0 :
        this.selectedFlight.price_offset + this.flightOffer.price_offset
    },

    outboundLuggage () {
      let result = false

      const pricings = this.flightOffer.travelerPricings[0]?.fareDetailsBySegment

      if (!pricings) {
        return false
      }

      const segmentId = parseInt(this.flightOffer.itinieraries[0].segments[0].id)
      const fare = pricings.find(f => parseInt(f.segmentId) === segmentId)

      if (fare?.includedCheckedBags?.weight || fare?.includedCheckedBags?.quantity) {
        result = true
      }

      return result
    },

    returnLuggage () {
      const pricings = this.flightOffer.travelerPricings[0]?.fareDetailsBySegment

      if (!pricings) {
        return false
      }

      const segmentId = parseInt(this.flightOffer.itinieraries[1].segments[0].id)
      const fare = pricings.find(f => parseInt(f.segmentId) === segmentId)

      if (fare?.includedCheckedBags?.weight || fare?.includedCheckedBags?.quantity) {
        return true
      }

      return false
    },
  },

  mounted () {
    this.windowWidth = window.innerWidth

    window.addEventListener('resize', this.resizeListener)
  },

  beforeUnmount () {
    window.removeEventListener('resize', this.resizeListener)
  },

  methods: {
    resizeListener () {
      if (window.innerWidth === this.windowWidth) {
        return
      }
      this.windowWidth = window.innerWidth

      if (window.innerWidth < 769) {
        this.expanded = false
      }
    },
  },
})
</script>

<style lang="scss" scoped>
@import "@layers/web/assets/scss/modules/charter/_flight-box";
</style>
